import { Avatar, Button, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { SearchUserEnriched } from 'lib-search-types';
import { MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { Component } from 'react';

import { getStatus } from 'components/presence/utils';
import { buttonize } from 'components/shared/util/buttonize';
import { getUserDisplayName } from 'utils/user';

import styles from './userItem.module.scss';

type PropTypes = {
  member: SearchUserEnriched;

  metaConversation: MetaConversation;
  numberTransferRequests: Array<any>;
  transferToAgentRequests: Array<any>;
  disabled: boolean;
  asyncStatus: string;
  highlighted: boolean;
  isAgent?: boolean;
  className?: string;

  onAssistedTransferClick: (payload: { requestToUser: User }) => void;
  onBlindTransferClick: (payload: { requestToUser: User }) => void;
};

type StateT = {
  isOpen: boolean;
};

export default class UserItem extends Component<PropTypes, StateT> {
  constructor(props: PropTypes) {
    super(props);
    this.state = {
      isOpen: false,
    };

    this.handleBlindTransfer = this.handleBlindTransfer.bind(this);
    this.handleAssistedTransfer = this.handleAssistedTransfer.bind(this);
  }

  toggle = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  handleBlindTransfer() {
    const { onBlindTransferClick, member } = this.props;
    onBlindTransferClick({ requestToUser: member });
  }

  handleAssistedTransfer() {
    const { onAssistedTransferClick, member } = this.props;
    onAssistedTransferClick({ requestToUser: member });
  }

  isPhoneChannelTurnedOff() {
    const { member, metaConversation } = this.props;
    return (
      metaConversation.conversation.initial_channel === 'pstnphone' &&
      !member.working_media_types?.includes('speak')
    );
  }

  isAgentOnThePhone() {
    const { member, metaConversation } = this.props;
    if (
      metaConversation.conversation.initial_channel === 'pstnphone' ||
      metaConversation.conversation.initial_channel === 'callback'
    ) {
      const phoneLoad = member.workloads_by_channel?.pstn_phone ?? 0;
      const callbackLoad = member.workloads_by_channel?.callback ?? 0;
      return phoneLoad + callbackLoad > 0;
    }
    return false;
  }

  render() {
    const {
      className,
      asyncStatus,
      disabled,
      highlighted,
      metaConversation,
      numberTransferRequests,
      member,
      transferToAgentRequests,
    } = this.props;
    const status = getStatus(member);

    const { isOpen } = this.state;

    const name = getUserDisplayName(member);
    const realNameOrThisAgent = name;
    let disableAllTransfers = disabled;
    let reassignDescription = `Transfer conversation #${metaConversation.conversation.id}.`;

    if (!disabled) {
      const requestToAgent = transferToAgentRequests.find(
        (request: any) =>
          request.metaConversation.conversation.id ===
          metaConversation.conversation.id,
      );

      const requestToNumber = numberTransferRequests.find(
        (request: any) =>
          request.metaConversation.conversation.id ===
          metaConversation.conversation.id,
      );

      if (metaConversation.requester?.id === member.id) {
        disableAllTransfers = true;
        reassignDescription = `${realNameOrThisAgent} is already in the conversation.`;
      } else {
        if (this.isPhoneChannelTurnedOff()) {
          disableAllTransfers = true;
          reassignDescription = `${realNameOrThisAgent} has the phone channel turned off.`;
        }

        if (this.isAgentOnThePhone()) {
          // We can not transfer a phone/callback if the agent has a phone/callback conversation.
          disableAllTransfers = true;
          reassignDescription = `${realNameOrThisAgent} is on the phone.`;
        }
      }

      if (requestToAgent !== undefined || requestToNumber !== undefined) {
        disableAllTransfers = true;
        reassignDescription = 'Request already sent waiting for response.';
      }
    } else if (asyncStatus === 'loading') {
      reassignDescription = 'Loading';
      disableAllTransfers = true;
    }

    const initialChannel =
      this.props.metaConversation.conversation.initial_channel;
    const disableAssistedtransfer =
      disableAllTransfers ||
      [
        'widgetchat',
        'messenger',
        'contactform',
        'voicemail',
        'email',
        'facebookmessenger',
      ].includes(initialChannel as any);

    const rootClass = classnames(
      {
        [styles.root]: true,
        [styles.rootHighlighted]: highlighted,
        [styles.rootWorking]: status === 'working',
        [styles.rootOffline]: status === 'offline',
        [styles.rootAway]: status === 'away',
        [styles.rootDim]: disableAllTransfers,
      },
      className,
    );

    return (
      <div
        className={rootClass}
        {...buttonize(this.toggle)}
        onClick={this.toggle}
      >
        <div className={styles.header}>
          <div className={styles.presenceDot} />
          <div className={styles.avatar}>
            <Avatar user={member} />
          </div>
          <span className={styles.name}>{name}</span>
          <IconButton
            icon={this.state.isOpen ? 'arrow-down' : 'arrow-right'}
            variant="tertiary"
            onClick={this.toggle}
          />
        </div>
        {isOpen && (
          <div className={styles.buttons}>
            <Tooltip content={reassignDescription}>
              <Button
                variant="secondary"
                disabled={disableAllTransfers}
                type="button"
                onClick={disableAllTransfers ? noop : this.handleBlindTransfer}
              >
                Transfer now
              </Button>
            </Tooltip>
            <Tooltip content={reassignDescription}>
              <Button
                variant="secondary"
                disabled={disableAssistedtransfer}
                type="button"
                onClick={
                  disableAssistedtransfer ? noop : this.handleAssistedTransfer
                }
              >
                Talk to {name} first
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}
